import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Home } from "lucide-react";
import { Footer } from "./Footer";
import { SEO } from "@/components/shared/SEO";
import { BrandMark } from "@/components/shared/BrandMark";

interface LegalLayoutProps {
  title: string;
  subtitle?: string;
  lastUpdated: string;
  children: React.ReactNode;
  testId?: string;
  seoPath?: string;
  seoDescription?: string;
  seoKeywords?: string[];
}

export const LegalLayout: React.FC<LegalLayoutProps> = ({
  title,
  subtitle,
  lastUpdated,
  children,
  testId,
  seoPath,
  seoDescription,
  seoKeywords,
}) => {
  const navigate = useNavigate();
  const canGoBack = typeof window !== "undefined" && window.history.length > 1;
  const handleBack = () => {
    if (canGoBack) navigate(-1);
    else navigate("/");
  };
  return (
    <div className="min-h-screen overflow-x-hidden bg-[color:var(--sg-bg)] flex flex-col" data-testid={testId}>
      <SEO
        title={title}
        description={seoDescription || subtitle || `${title} — SociBaba legal & policies.`}
        keywords={seoKeywords}
        path={seoPath}
        type="article"
      />
      <header className="border-b border-[color:var(--sg-border)] bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div>
              <BrandMark
                showIcon
                iconClassName="h-9 w-9 mr-2 inline-block align-middle dark:invert"
                className="font-serif-display text-lg font-bold tracking-tight leading-none flex items-center"
              />
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--sg-muted)] mt-1">
                Social Intelligence
              </div>
            </div>
          </Link>
          <div className="hidden sm:flex items-center gap-4 text-sm">
            <Link to="/login" className="text-[color:var(--sg-fg)] hover:text-[color:var(--sg-primary)] font-semibold">
              Log in
            </Link>
            <Link
              to="/signup"
              className="bg-[color:var(--sg-fg)] text-white px-4 py-2 rounded-lg hover:bg-black font-semibold"
            >
              Get started
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-6 lg:px-10 py-12">
        <div className="mb-6 flex items-center gap-2" data-testid="legal-nav-actions">
          <button
            type="button"
            onClick={handleBack}
            data-testid="legal-back-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--sg-border)] bg-white hover:bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)] text-sm font-medium px-3.5 py-1.5 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <Link
            to="/"
            data-testid="legal-home-btn"
            className="inline-flex items-center gap-1.5 rounded-full border border-[color:var(--sg-border)] bg-white hover:bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)] text-sm font-medium px-3.5 py-1.5 transition-colors"
          >
            <Home className="h-4 w-4" />
            Home
          </Link>
        </div>
        <div className="sg-label mb-3">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mb-3">
          {title}
        </h1>
        {subtitle && (
          <p className="text-base text-[color:var(--sg-muted)] mb-3 leading-relaxed">
            {subtitle}
          </p>
        )}
        <div className="text-xs text-[color:var(--sg-muted)] mb-10">
          Last updated: {lastUpdated}
        </div>
        <article className="prose prose-slate max-w-none text-[color:var(--sg-fg)] leading-relaxed">
          {children}
        </article>
      </main>

      <Footer />
    </div>
  );
};

interface SectionProps {
  n: string | number;
  title: string;
  children: React.ReactNode;
}

export const Section: React.FC<SectionProps> = ({ n, title, children }) => (
  <section className="mt-10">
    <h2 className="font-display text-xl font-semibold tracking-tight text-[color:var(--sg-fg)] mb-3">
      <span className="text-[color:var(--sg-muted)] font-mono text-sm mr-2">{String(n).padStart(2, "0")}</span>
      {title}
    </h2>
    <div className="space-y-3 text-sm">{children}</div>
  </section>
);
